import useWidth from "app/hooks/useWidth";
import { useTheme } from "app/shared";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { selectSearchQuery, setSearchQuery } from "store/slice/search.slice";
import Button from "../button/button";

import SocialIcons from "../ido-details/social-icons/social-icons";
import Tag from "../ido-details/tag/tag";
import NetworkIcons from "../ido-details/ supported-networks-icons/ supported-networks-icons";
import SubscribeFormSmall from "../subscribe-form/sub-form-small";
import styles from "./main-projects.module.scss";

interface Props {
  data?: { items?: any[] };
  title: string;
  description: string;
  search?: boolean;
  filters?: { label: string; value: string }[];
  isStats?: boolean;
}

const IdoProject: FC<Props> = ({
  data = { items: [] },
  title,
  description,
  search = true,
  isStats = false,
}) => {
  const dispatch = useDispatch();
  const searchQuery = useSelector(selectSearchQuery);
  const history = useHistory();
  const { isMobile, isTablet } = useWidth();
  const { theme } = useTheme();

  const [activeTab, setActiveTab] = useState<string>("All");
  const [visibleItems, setVisibleItems] = useState<number>(6);

  useEffect(() => {
    if (isTablet) setVisibleItems(2);
  }, [isTablet]);

  const tabs = [
    "SmartWhitelist Live",
    "IDO Live",
    "SmartWhitelist Upcoming",
    "IDO Upcoming",
    "Completed",
    "All",
  ];

  const tabColorClass = (tab: string) => {
    if (tab === "SmartWhitelist Live" || tab === "IDO Live")
      return styles.green;
    if (tab.includes("Upcoming")) return styles.yellow;
    if (tab === "Completed") return styles.red;
    return styles.white;
  };

  const handleCardClick = (id: string) => {
    history.push(`/ido/${id}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(e.target.value));
  };

  const filteredItems = useMemo(() => {
    const itemsArray: any[] = Array.isArray(data.items) ? data.items : [];
    const now = new Date();

    const isWithinDateRange = (start?: string, end?: string) => {
      if (!start || !end) return false;
      const from = new Date(start);
      const to = new Date(end);
      return from <= now && now <= to;
    };

    const byTab = itemsArray.filter((item) => {
      switch (activeTab) {
        case "All":
          return true;
        case "SmartWhitelist Live":
          return (
            item.currentStage === "Whitelist" &&
            isWithinDateRange(
              item.preWhitelistDate?.start,
              item.preWhitelistDate?.end
            )
          );
        case "IDO Live":
          return (
            item.currentStage === "Sale" &&
            isWithinDateRange(item.idoDate?.start, item.idoDate?.end)
          );
        case "SmartWhitelist Upcoming":
          return (
            item.currentStage === "Whitelist" &&
            new Date(item.preWhitelistDate?.start || "") > now
          );
        case "IDO Upcoming":
          return (
            item.currentStage === "Whitelist" &&
            new Date(item.preWhitelistDate?.end || "") <= now &&
            new Date(item.idoDate?.start || "") >= now
          );
        case "Completed":
          return (
            item.currentStage === "Sale" &&
            new Date(item.idoDate?.end || "") <= now
          );
        default:
          return false;
      }
    });

    return byTab.filter((item) => {
      const title = item.title?.toLowerCase() ?? "";
      return !searchQuery || title.includes(searchQuery.toLowerCase());
    });
  }, [data.items, activeTab, searchQuery]);

  const loadMoreItems = () => {
    setVisibleItems((prev) => prev + (isTablet ? 2 : 3));
  };

  return (
    <div
      className={`${styles.projectComponent} ${
        theme === "dark" ? styles.darkTheme : ""
      }`}
    >
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.description}>{description}</p>

      {search && (
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
          className={`${styles.searchInput} ${
            theme === "dark" ? styles.darkTheme : ""
          }`}
        />
      )}

      <div className={styles.filterContainer}>
        {tabs.map((tab) => (
          <Button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`
              ${styles.filterButton}
              ${activeTab === tab ? styles.activeFilter : ""}
              ${tabColorClass(tab)}
            `}
          >
            {tab}
          </Button>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div>
          <h2 className={styles.noIdoTitle}>
            🚀 The IDO isn’t open right now, but don’t miss your chance!
          </h2>
          <span className={styles.noIdoTitle}>
            ⬇️ Leave your email, and we’ll notify you as soon as it’s live!
          </span>
        </div>
      )}

      <div className={styles.cardList}>
        {filteredItems.slice(0, visibleItems).map((item) => {
          const preWLStart = item.preWhitelistDate?.start;
          const preWLEnd = item.preWhitelistDate?.end;
          const saleStart = item.idoDate?.start;
          const saleEnd = item.idoDate?.end;

          return (
            <div
              key={item._id}
              className={`${styles.card} ${
                theme === "dark" ? styles.darkTheme : ""
              }`}
            >
              <div
                className={styles.cardHeader}
                style={{
                  backgroundImage: `url(${
                    item.media?.find((m: any) => m.label === "bg")?.value || ""
                  })`,
                }}
              />
              <img
                src={
                  item.media?.find((m: any) => m.label === "logo")?.value || ""
                }
                alt={`${item.title} logo`}
                className={styles.cardLogo}
              />
              <div className={styles.cardBody}>
                <h2 className={styles.cardTitle}>{item.title}</h2>
                <p>{item.description}</p>
                <div className={styles.idoCardWrapper}>
                  {!isStats && (
                    <>
                      <div className={styles.IdoWrapper}>
                        <p className={styles.keymetrics}>Key Metrics:</p>
                        <div className={styles.cardInfoWrapper}>
                          {(item.frontEndData?.[0]?.tokenMetrics || [])
                            .filter((_: any, index: number) => {
                              const now = new Date();
                              const wlStart = new Date(
                                item.preWhitelistDate?.start || ""
                              );
                              return (
                                item.tokenTicker === "$TDS" ||
                                now >= wlStart ||
                                index < 4
                              );
                            })
                            .map((metric: any) => (
                              <div
                                className={styles.cardInfo}
                                key={metric.label}
                              >
                                <p>{metric.label}:</p>
                                <p>{metric.value || "N/A"}</p>
                              </div>
                            ))}
                        </div>
                        <div className={styles.cardInfo}>
                          <p>Networks:</p>
                          <NetworkIcons
                            networks={item.supportedNetworks || []}
                          />
                        </div>
                      </div>

                      <Tag
                        title={item.title}
                        whitelistStartDate={preWLStart}
                        whitelistEndDate={preWLEnd}
                        saleStartDate={saleStart}
                        saleEndDate={saleEnd}
                        stage={item.currentStage}
                      />
                      {item.frontEndData?.[4]?.socialLinks && (
                        <div className={styles.socialIcons}>
                          <SocialIcons
                            links={item.frontEndData[4].socialLinks}
                          />
                        </div>
                      )}

                      {preWLStart &&
                        new Date() < new Date(preWLStart) &&
                        item.tokenTicker !== "$TDS" && (
                          <SubscribeFormSmall
                            id={
                              item.frontEndData?.[12]?.brevoId?.[0]?.value || ""
                            }
                            isPreWl
                          />
                        )}

                      <Button
                        className={styles.projectBtn}
                        onClick={() => handleCardClick(item._id)}
                        disabled={
                          preWLStart &&
                          new Date() < new Date(preWLStart) &&
                          item.tokenTicker !== "$TDS"
                        }
                      >
                        {item.ctaButtonText || "Check Details"}
                      </Button>
                    </>
                  )}

                  {isStats && (
                    <div className={styles.statWrspper}>
                      <Button
                        variant="blue"
                        className={styles.projectBtn}
                        onClick={() =>
                          history.push(`/ref_statistics/${item._id}`)
                        }
                      >
                        Get My Statistics
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {filteredItems.length > visibleItems && (
          <Button
            variant={isStats ? "blue" : "pink"}
            onClick={loadMoreItems}
            className={styles.loadMoreBtn}
          >
            Load More
          </Button>
        )}
      </div>
    </div>
  );
};

export default IdoProject;
